/**
 * Presentation-only holiday calendar. Product prices and discount rules are never
 * changed here. Use Shopify's response Date, not an untrusted device clock or a
 * cached Liquid `now`, so the storefront cannot advertise a promotion early.
 */
(() => {
  if (window.HolidaySaleClock) return;
  const campaigns = [
    { slug: 'holiday-preview', percent: 15, start: Date.parse('2026-11-10T05:00:00Z'), end: Date.parse('2026-11-20T05:00:00Z') },
    { slug: 'black-friday', percent: 30, start: Date.parse('2026-11-20T05:00:00Z'), end: Date.parse('2026-12-01T05:00:00Z') },
    { slug: 'christmas', percent: 20, start: Date.parse('2026-12-01T05:00:00Z'), end: Date.parse('2026-12-25T05:00:00Z') }
  ];
  let serverAnchor = null;
  let elapsedAnchor = 0;
  let boundaryTimer;
  let requestInProgress = false;
  let current = { slug: 'none', percent: 0 };
  const phaseAt = now => campaigns.find(phase => now >= phase.start && now < phase.end) || { slug: 'none', percent: 0 };
  const serverNow = () => serverAnchor === null ? null : serverAnchor + (performance.now() - elapsedAnchor);

  function update() {
    const now = serverNow();
    const next = now === null ? { slug: 'none', percent: 0 } : phaseAt(now);
    document.documentElement.dataset.holidaySale = next.slug;
    if (current.slug !== next.slug) {
      current = { slug: next.slug, percent: next.percent };
      document.dispatchEvent(new CustomEvent('holiday:change', { detail: current }));
    }
    clearTimeout(boundaryTimer);
    if (now !== null) {
      const boundary = campaigns.flatMap(phase => [phase.start, phase.end]).find(time => time > now);
      if (boundary) boundaryTimer = setTimeout(update, Math.min(boundary - now + 100, 2147480000));
    }
  }

  async function sync() {
    if (requestInProgress) return;
    requestInProgress = true;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root, {
        method: 'HEAD', cache: 'no-store', credentials: 'same-origin', signal: controller.signal
      });
      if (!response.ok) throw new Error('Shopify clock unavailable');
      const serverDate = Date.parse(response.headers.get('date') || '');
      const age = Math.max(0, Number(response.headers.get('age') || 0));
      if (!Number.isFinite(serverDate) || !Number.isFinite(age)) throw new Error('Invalid Shopify clock');
      // The HTTP Date has one-second precision. Do not add network RTT, which could
      // make the promotion begin before Shopify's inclusive start boundary.
      serverAnchor = serverDate + age * 1000;
      elapsedAnchor = performance.now();
      update();
    } catch {
      // Fail safe: base prices remain visible until a trusted clock is available.
      // If already synchronized, retain the monotonic clock and its end timer.
      update();
    } finally {
      clearTimeout(timeout);
      requestInProgress = false;
    }
  }

  window.HolidaySaleClock = {
    get current() { return { ...current }; },
    percent() { update(); return current.percent; }
  };
  update();
  sync();
  const resync = setInterval(sync, 300000);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) { update(); sync(); } });
  window.addEventListener('pageshow', () => { update(); sync(); });
  window.addEventListener('pagehide', event => { if (!event.persisted) { clearInterval(resync); clearTimeout(boundaryTimer); } });
})();
